import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Phone, MessageSquare, Mail, Calendar, Zap, Settings2,
  Clock, Edit2, Trash2, Plus, RefreshCw, Activity,
  CheckCircle2, XCircle, Sparkles, ChevronDown, ChevronUp,
  ToggleLeft, Power, AlertCircle, Globe
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import LeadFormsSection from "../../components/automations/LeadFormsSection";
import TemplateLivePreview from "../../components/settings/TemplateLivePreview";

const CATEGORIES = [
  { id: "call_flow", name: "Call Flows", icon: Phone, accent: "#3b82f6" },
  { id: "sms", name: "SMS", icon: MessageSquare, accent: "#22c55e" },
  { id: "email", name: "Email", icon: Mail, accent: "#a855f7" },
  { id: "job_management", name: "Job Mgmt", icon: Calendar, accent: "#f97316" },
];

const TEMPLATE_VARS = [
  // Customer
  "{{customer_name}}", "{{customer_phone}}", "{{customer_email}}", "{{customer_address}}",
  // Job
  "{{job_id}}", "{{job_title}}", "{{job_type}}", "{{job_date}}", "{{job_time}}",
  "{{scheduled_date}}", "{{scheduled_time}}",
  "{{schedule_start_time}}", "{{schedule_end_time}}", "{{schedule_time_range}}",
  "{{job_status}}", "{{job_notes}}", "{{job_address}}", "{{job_city}}", "{{job_state}}",
  "{{job_zip}}", "{{job_price}}", "{{job_source}}", "{{time_until}}",
  // Technician
  "{{technician_name}}", "{{technician_phone}}",
  // Business
  "{{business_name}}", "{{business_phone}}", "{{business_email}}", "{{location_name}}",
  // Financial
  "{{invoice_number}}", "{{invoice_amount}}", "{{quote_amount}}", "{{payment_amount}}", "{{balance_due}}",
  // Links
  "{{assignment_link}}", "{{review_link}}", "{{payment_link}}",
  // Call Masking
  "{{masked_call_number_with_pin}}",
];

const AutomationsPage = () => {
  const { isDark } = useTheme();
  const { timezone, formatDate, formatTime } = useTimezone();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("call_flow");
  const [editDialog, setEditDialog] = useState({ open: false, automation: null });
  const [createDialog, setCreateDialog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState({ running: false, pending_automations: 0 });
  const [showActivity, setShowActivity] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAutomations();
    fetchLogs();
  }, []);

  const fetchAutomations = async () => {
    try {
      const res = await axios.get(`${API}/automations`, { headers });
      setAutomations(res.data);
    } catch (error) {
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const [logsRes, scheduledRes, statusRes] = await Promise.all([
        axios.get(`${API}/automations/logs/recent`, { headers }),
        axios.get(`${API}/automations/scheduled`, { headers }),
        axios.get(`${API}/automations/scheduler/status`, { headers })
      ]);
      setLogs(logsRes.data);
      setScheduled(scheduledRes.data);
      setSchedulerStatus(statusRes.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const toggleAutomation = async (automationId) => {
    try {
      const res = await axios.patch(`${API}/automations/${automationId}/toggle`, {}, { headers });
      setAutomations(prev =>
        prev.map(a => a.automation_id === automationId ? { ...a, is_enabled: res.data.is_enabled } : a)
      );
      toast.success(res.data.is_enabled ? "Automation enabled" : "Automation disabled");
    } catch (error) {
      toast.error("Failed to toggle automation");
    }
  };

  const updateAutomation = async (automationId, data) => {
    try {
      const res = await axios.put(`${API}/automations/${automationId}`, data, { headers });
      setAutomations(prev =>
        prev.map(a => a.automation_id === automationId ? res.data : a)
      );
      toast.success("Automation updated");
      setEditDialog({ open: false, automation: null });
    } catch (error) {
      toast.error("Failed to update automation");
    }
  };

  const createAutomation = async (data) => {
    try {
      const res = await axios.post(`${API}/automations`, data, { headers });
      setAutomations(prev => [...prev, res.data]);
      toast.success("Automation created");
      setCreateDialog(false);
    } catch (error) {
      toast.error("Failed to create automation");
    }
  };

  const deleteAutomation = async (automationId) => {
    if (!window.confirm("Delete this automation?")) return;
    try {
      await axios.delete(`${API}/automations/${automationId}`, { headers });
      setAutomations(prev => prev.filter(a => a.automation_id !== automationId));
      toast.success("Automation deleted");
    } catch (error) {
      toast.error("Failed to delete automation");
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("This will reset all automations to defaults. Continue?")) return;
    try {
      await axios.post(`${API}/automations/reset-defaults`, {}, { headers });
      await fetchAutomations();
      toast.success("Automations reset to defaults");
    } catch (error) {
      toast.error("Failed to reset automations");
    }
  };

  const getByCategory = (cat) => automations.filter(a => a.category === cat);
  const enabledCount = (cat) => getByCategory(cat).filter(a => a.is_enabled).length;
  const totalCount = (cat) => getByCategory(cat).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#FFCC00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" data-testid="automations-title">
            <Zap className="w-6 h-6 text-[#FFCC00]" />
            Automations
            {schedulerStatus.running && (
              <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-xs ml-2" data-testid="scheduler-status">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-pulse" />
                Scheduler Active
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Set up and manage automated workflows for your business</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchLogs(); setShowActivity(!showActivity); }}
            className="text-sm"
            data-testid="activity-log-btn"
          >
            <Activity className="w-4 h-4 mr-1.5" />
            Activity
            {showActivity ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
          <Button
            size="sm"
            onClick={() => setCreateDialog(true)}
            className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 text-sm"
            data-testid="create-automation-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Automation
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CATEGORIES.map(cat => {
          const enabled = enabledCount(cat.id);
          const total = totalCount(cat.id);
          return (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`rounded-xl border p-4 text-left transition-all ${
                activeTab === cat.id
                  ? isDark
                    ? "border-[#FFCC00]/40 bg-[#FFCC00]/5"
                    : "border-[#FFCC00]/50 bg-[#FFCC00]/5 shadow-sm"
                  : isDark
                    ? "border-white/10 bg-white/[0.03] hover:border-white/20"
                    : "border-gray-200 bg-white hover:border-gray-300 shadow-sm"
              }`}
              data-testid={`stat-card-${cat.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{cat.name}</span>
                <cat.icon className="w-4 h-4" style={{ color: cat.accent }} />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-foreground">{enabled}</span>
                <span className="text-sm text-muted-foreground">/ {total}</span>
              </div>
              <div className="mt-2 h-1 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: total > 0 ? `${(enabled / total) * 100}%` : "0%", backgroundColor: cat.accent }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Activity Panel */}
      {showActivity && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className={isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-gray-200 shadow-sm"}>
            <CardHeader className="pb-2">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#FFCC00]" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No automation activity yet</p>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {logs.slice(0, 10).map((log, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}>
                      {log.status === "success" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      ) : log.status === "scheduled" ? (
                        <Clock className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-foreground block truncate">{log.automation_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {log.action_type?.replace('_', ' ')}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTime(log.executed_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-gray-200 shadow-sm"}>
            <CardHeader className="pb-2">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                Scheduled ({scheduled.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scheduled.length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No pending automations</p>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {scheduled.slice(0, 10).map((s, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}>
                      <Clock className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-foreground block truncate">
                          {s.context?.customer_name || "Customer"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {s.context?.service_type || "Service"}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-muted-foreground block">
                          {formatDate(s.scheduled_for)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(s.scheduled_for)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList className={`p-1 ${isDark ? "bg-white/[0.05] border border-white/10" : "bg-gray-100 border border-gray-200"}`}>
            {CATEGORIES.map(cat => (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black text-sm gap-1.5"
                data-testid={`tab-${cat.id}`}
              >
                <cat.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{cat.name}</span>
                <Badge variant="outline" className="ml-1 text-[10px] h-5 border-current px-1.5">
                  {enabledCount(cat.id)}
                </Badge>
              </TabsTrigger>
            ))}
            <TabsTrigger
              value="lead_forms"
              className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black text-sm gap-1.5"
              data-testid="tab-lead_forms"
            >
              <Globe className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Lead Forms</span>
            </TabsTrigger>
          </TabsList>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetDefaults}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="reset-defaults-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
        </div>

        {CATEGORIES.map(cat => (
          <TabsContent key={cat.id} value={cat.id} className="space-y-3 mt-4">
            {getByCategory(cat.id).length === 0 ? (
              <Card className={isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-gray-200 shadow-sm"}>
                <CardContent className="p-10 text-center">
                  <cat.icon className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">No {cat.name.toLowerCase()} automations configured</p>
                  <Button
                    size="sm"
                    className="mt-4 bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90"
                    onClick={() => setCreateDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Create One
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {getByCategory(cat.id).map(automation => (
                  <AutomationCard
                    key={automation.automation_id}
                    automation={automation}
                    isDark={isDark}
                    onToggle={toggleAutomation}
                    onEdit={() => setEditDialog({ open: true, automation })}
                    onDelete={() => deleteAutomation(automation.automation_id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}

        <TabsContent value="lead_forms" className="mt-4">
          <LeadFormsSection isDark={isDark} headers={headers} />
        </TabsContent>
      </Tabs>

      {/* Pro Tips */}
      <Card className={`border ${isDark ? "bg-gradient-to-r from-[#FFCC00]/5 to-transparent border-[#FFCC00]/20" : "bg-gradient-to-r from-amber-50 to-transparent border-amber-200/60"}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${isDark ? "bg-[#FFCC00]/10" : "bg-amber-100"}`}>
              <Sparkles className="w-5 h-5 text-[#FFCC00]" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold text-sm mb-1.5">Pro Tips</h3>
              <ul className="text-muted-foreground text-xs space-y-1 leading-relaxed">
                <li>Start with appointment reminders - highest impact on reducing no-shows</li>
                <li>Enable review requests to build your online reputation automatically</li>
                <li>Use call forwarding during business hours and voicemail after hours</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <EditDialog
        editDialog={editDialog}
        setEditDialog={setEditDialog}
        updateAutomation={updateAutomation}
        isDark={isDark}
      />

      {/* Create Dialog */}
      <CreateDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        onSubmit={createAutomation}
        isDark={isDark}
        activeTab={activeTab}
      />
    </div>
  );
};

/* ──────── Automation Card ──────── */
const AutomationCard = ({ automation, isDark, onToggle, onEdit, onDelete }) => {
  const actionLabel = automation.action?.type?.replace(/_/g, ' ') || "action";
  const timing = automation.action?.timing
    ? `${automation.action.timing.value} ${automation.action.timing.unit}`
    : "Instant";

  return (
    <div
      className={`group rounded-xl border p-4 transition-all ${
        automation.is_enabled
          ? isDark
            ? "border-[#FFCC00]/30 bg-[#FFCC00]/[0.03]"
            : "border-[#FFCC00]/40 bg-[#FFCC00]/[0.02] shadow-sm"
          : isDark
            ? "border-white/10 bg-white/[0.02] hover:border-white/20"
            : "border-gray-200 bg-white hover:border-gray-300 shadow-sm"
      }`}
      data-testid={`automation-card-${automation.automation_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-foreground truncate" data-testid={`automation-name-${automation.automation_id}`}>
              {automation.name}
            </h4>
            {automation.is_enabled && (
              <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-[10px] px-1.5 h-5">
                Active
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 mb-3">
            {automation.description}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 text-muted-foreground border-border">
              <Zap className="w-2.5 h-2.5" />
              <span className="capitalize">{actionLabel}</span>
            </Badge>
            <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 text-muted-foreground border-border">
              <Clock className="w-2.5 h-2.5" />
              {timing}
            </Badge>
            {automation.execution_count > 0 && (
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 text-muted-foreground border-border">
                <Activity className="w-2.5 h-2.5" />
                {automation.execution_count} runs
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Switch
            checked={automation.is_enabled}
            onCheckedChange={() => onToggle(automation.automation_id)}
            className="data-[state=checked]:bg-[#FFCC00]"
            data-testid={`toggle-${automation.automation_id}`}
          />
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={onEdit}
              data-testid={`edit-${automation.automation_id}`}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-red-500"
              onClick={onDelete}
              data-testid={`delete-${automation.automation_id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ──────── Edit Dialog ──────── */
const EditDialog = ({ editDialog, setEditDialog, updateAutomation, isDark }) => {
  const [formData, setFormData] = useState({
    name: "", description: "", template_content: "",
    admin_template_content: "", tech_template_content: "",
    timing_value: 24, timing_unit: "hours", is_enabled: false
  });

  // True when this automation uses the dual-template "send_dual_sms"
  // action type (currently only "Job Not Responded Alert"). The dialog
  // swaps the single Textarea for two labelled Textareas when this is on.
  const isDualSms = editDialog.automation?.action?.type === "send_dual_sms";

  useEffect(() => {
    if (editDialog.automation) {
      setFormData({
        name: editDialog.automation.name || "",
        description: editDialog.automation.description || "",
        template_content: editDialog.automation.action?.template_content || "",
        admin_template_content: editDialog.automation.action?.admin_template_content || "",
        tech_template_content: editDialog.automation.action?.tech_template_content || "",
        timing_value: editDialog.automation.action?.timing?.value || 24,
        timing_unit: editDialog.automation.action?.timing?.unit || "hours",
        is_enabled: editDialog.automation.is_enabled || false
      });
    }
  }, [editDialog.automation]);

  const handleSave = () => {
    const update = {
      name: formData.name,
      description: formData.description,
      action: {
        ...editDialog.automation.action,
        ...(isDualSms
          ? {
              admin_template_content: formData.admin_template_content,
              tech_template_content: formData.tech_template_content,
            }
          : { template_content: formData.template_content }),
        timing: editDialog.automation.action?.timing ? {
          value: parseInt(formData.timing_value),
          unit: formData.timing_unit
        } : undefined
      },
      is_enabled: formData.is_enabled
    };
    updateAutomation(editDialog.automation.automation_id, update);
  };

  return (
    <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, automation: open ? editDialog.automation : null })}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-automation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#FFCC00]" />
            Edit Automation
          </DialogTitle>
          <DialogDescription>Customize this automation's behavior</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="edit-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              data-testid="edit-description-input"
            />
          </div>

          {isDualSms ? (
            <>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-2">
                  Admin &amp; Dispatcher Message
                  <Badge variant="outline" className="text-[10px]">sent to admins + dispatchers</Badge>
                </Label>
                <Textarea
                  value={formData.admin_template_content}
                  onChange={(e) => setFormData({ ...formData, admin_template_content: e.target.value })}
                  className="min-h-[100px] text-sm"
                  placeholder="Job #{{job_id}} — {{customer_name}} has not been responded to by {{technician_name}}…"
                  data-testid="edit-admin-template-textarea"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-2">
                  Technician Message
                  <Badge variant="outline" className="text-[10px]">sent to the assigned tech</Badge>
                </Label>
                <Textarea
                  value={formData.tech_template_content}
                  onChange={(e) => setFormData({ ...formData, tech_template_content: e.target.value })}
                  className="min-h-[100px] text-sm"
                  placeholder="Reminder: You have an unresponded job assigned to you…"
                  data-testid="edit-tech-template-textarea"
                />
                <p className="text-[10px] text-muted-foreground/70">
                  Supported merge tags:
                  {" "}
                  {["{{job_id}}", "{{customer_name}}", "{{technician_name}}", "{{time_elapsed}}"].map((v) => (
                    <Badge
                      key={v}
                      variant="outline"
                      className="text-[10px] ml-1 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => setFormData({ ...formData, tech_template_content: formData.tech_template_content + " " + v })}
                    >
                      {v}
                    </Badge>
                  ))}
                </p>
              </div>
            </>
          ) : editDialog.automation?.action?.template_content !== undefined && (
            <div className="space-y-1.5">
              <Label>Message Template</Label>
              <Textarea
                value={formData.template_content}
                onChange={(e) => setFormData({ ...formData, template_content: e.target.value })}
                className="min-h-[100px] text-sm"
                placeholder="Enter your message template..."
                data-testid="edit-template-textarea"
              />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {TEMPLATE_VARS.map(v => (
                  <Badge
                    key={v}
                    variant="outline"
                    className="text-[10px] cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => setFormData({ ...formData, template_content: formData.template_content + " " + v })}
                  >
                    {v}
                  </Badge>
                ))}
              </div>
              <div className="pt-1">
                <TemplateLivePreview content={formData.template_content} />
              </div>
            </div>
          )}

          {editDialog.automation?.action?.timing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{isDualSms ? "Alert After (Value)" : "Timing Value"}</Label>
                <Input
                  type="number"
                  value={formData.timing_value}
                  onChange={(e) => setFormData({ ...formData, timing_value: e.target.value })}
                  data-testid="edit-timing-value"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{isDualSms ? "Alert After (Unit)" : "Timing Unit"}</Label>
                <Select value={formData.timing_unit} onValueChange={(v) => setFormData({ ...formData, timing_unit: v })}>
                  <SelectTrigger data-testid="edit-timing-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between py-1">
            <Label>Enable Automation</Label>
            <Switch
              checked={formData.is_enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
              className="data-[state=checked]:bg-[#FFCC00]"
              data-testid="edit-enabled-switch"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setEditDialog({ open: false, automation: null })} data-testid="edit-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" data-testid="edit-save-btn">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ──────── Create Dialog ──────── */
const CreateDialog = ({ open, onClose, onSubmit, isDark, activeTab }) => {
  const [form, setForm] = useState({
    category: activeTab,
    name: "",
    description: "",
    trigger_event: "",
    action_type: "send_sms",
    template_content: "",
    target: "customer",
    has_timing: false,
    timing_value: 24,
    timing_unit: "hours",
    is_enabled: true,
  });

  useEffect(() => {
    if (open) setForm(f => ({ ...f, category: activeTab }));
  }, [open, activeTab]);

  const TRIGGER_EVENTS = [
    { value: "job_created", label: "Job Created" },
    { value: "job_status_change", label: "Job Status Change" },
    { value: "appointment_scheduled", label: "Appointment Scheduled" },
    { value: "new_customer", label: "New Customer" },
    { value: "invoice_sent", label: "Invoice Sent" },
    { value: "invoice_paid", label: "Invoice Paid" },
    { value: "quote_sent", label: "Quote Sent" },
    { value: "new_sms_received", label: "New SMS Received" },
    { value: "incoming_call", label: "Incoming Call" },
    { value: "voicemail_received", label: "Voicemail Received" },
  ];

  const ACTION_TYPES = [
    { value: "send_sms", label: "Send SMS" },
    { value: "send_email", label: "Send Email" },
    { value: "send_notification", label: "Send Notification" },
    { value: "forward_call", label: "Forward Call" },
    { value: "voicemail", label: "Voicemail" },
    { value: "ivr_menu", label: "IVR Menu" },
    { value: "auto_assign", label: "Auto-Assign" },
  ];

  const handleSubmit = () => {
    if (!form.name || !form.trigger_event) {
      toast.error("Name and trigger event are required");
      return;
    }
    onSubmit({
      category: form.category,
      name: form.name,
      description: form.description,
      trigger: { event: form.trigger_event, conditions: {} },
      action: {
        type: form.action_type,
        target: form.target,
        template_content: form.template_content || undefined,
        timing: form.has_timing ? { value: parseInt(form.timing_value), unit: form.timing_unit } : undefined,
      },
      is_enabled: form.is_enabled,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-automation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#FFCC00]" />
            Create Automation
          </DialogTitle>
          <DialogDescription>Set up a new automated workflow</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="create-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Action Type</Label>
              <Select value={form.action_type} onValueChange={(v) => setForm({ ...form, action_type: v })}>
                <SelectTrigger data-testid="create-action-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_TYPES.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Follow-up SMS after job"
              data-testid="create-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does this automation do?"
              data-testid="create-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Trigger Event *</Label>
              <Select value={form.trigger_event} onValueChange={(v) => setForm({ ...form, trigger_event: v })}>
                <SelectTrigger data-testid="create-trigger-event">
                  <SelectValue placeholder="Select trigger..." />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_EVENTS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Target</Label>
              <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                <SelectTrigger data-testid="create-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="technician">Technician</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {(form.action_type === "send_sms" || form.action_type === "send_email") && (
            <div className="space-y-1.5">
              <Label>Message Template</Label>
              <Textarea
                value={form.template_content}
                onChange={(e) => setForm({ ...form, template_content: e.target.value })}
                className="min-h-[80px] text-sm"
                placeholder="Hi {customer_name}, ..."
                data-testid="create-template-textarea"
              />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {TEMPLATE_VARS.map(v => (
                  <Badge
                    key={v}
                    variant="outline"
                    className="text-[10px] cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => setForm({ ...form, template_content: form.template_content + " " + v })}
                  >
                    {v}
                  </Badge>
                ))}
              </div>
              <div className="pt-1">
                <TemplateLivePreview content={form.template_content} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label>Add Delay / Timing</Label>
            <Switch
              checked={form.has_timing}
              onCheckedChange={(checked) => setForm({ ...form, has_timing: checked })}
              data-testid="create-timing-toggle"
            />
          </div>

          {form.has_timing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Delay Value</Label>
                <Input
                  type="number"
                  value={form.timing_value}
                  onChange={(e) => setForm({ ...form, timing_value: e.target.value })}
                  data-testid="create-timing-value"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Select value={form.timing_unit} onValueChange={(v) => setForm({ ...form, timing_unit: v })}>
                  <SelectTrigger data-testid="create-timing-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between py-1">
            <Label>Enable on Create</Label>
            <Switch
              checked={form.is_enabled}
              onCheckedChange={(checked) => setForm({ ...form, is_enabled: checked })}
              className="data-[state=checked]:bg-[#FFCC00]"
              data-testid="create-enabled-switch"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="create-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" data-testid="create-submit-btn">
            Create Automation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutomationsPage;
